import React from 'react';
import {
  Cloud, CloudOff, CloudSnow, Info, Sun,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { IWeatherItem } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter, useTemperatureFormatter } from '@/i18n/unitFormat';
import { Text } from '@/components/ui/text';
import { cn } from '@/util/cn';
import { SHORT_DATE_FORMAT } from '@/util/formatDate';
import { RecentSnowfallState } from '@/util/recentSnowfallFreshness';
import { MiniSectionHeading } from './atoms';

type DaySnow = { date: string; snow: number };

// Snowfall amount → weather icon. The production `forecast` endpoint
// only provides (date, inches), so we pick an icon from the inches.
const iconForSnow = (snow: number) => {
  if (snow >= 3) return CloudSnow;
  if (snow > 0) return Cloud;
  return Sun;
};

// Recent-bar label. "Today" only when the date is literally today; stale
// reports use an unambiguous "MMM d" ("Apr 18") because a bare weekday
// repeats and would re-create the staleness confusion we're fixing.
const barLabel = (iso: string, state: RecentSnowfallState, todayLabel: string) => {
  const day = DateTime.fromISO(iso);
  if (!day.isValid) return '';
  if (day.hasSame(DateTime.now(), 'day')) return todayLabel;
  return state === 'stale' ? day.toFormat(SHORT_DATE_FORMAT) : day.toFormat('EEE');
};

// Forecast label is unchanged: the forecast is live, so the first cell
// is genuinely "Today".
const forecastDayLabel = (iso: string, isFirst: boolean, todayLabel: string) => {
  if (isFirst) return todayLabel;
  return DateTime.fromISO(iso).toFormat('EEE');
};

// Bar fill class avoids nested ternaries (no-nested-ternary).
const barFillClass = (isStale: boolean, isRecent: boolean): string => {
  if (isStale) return isRecent ? 'bg-muted-foreground/50' : 'bg-muted-foreground/25';
  return isRecent ? 'bg-primary' : 'bg-primary/30';
};

export const SnowfallRow = ({
  recent, forecast, weatherDays, snowHistoryHref, fullWeatherHref, state, reportedAtLabel,
}: {
  recent: DaySnow[];
  forecast: DaySnow[];
  weatherDays: IWeatherItem[];
  snowHistoryHref: string;
  fullWeatherHref: string;
  state: RecentSnowfallState;
  reportedAtLabel: string;
}) => {
  const { t } = useOTSTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');
  const tempFmt = useTemperatureFormatter('temperature');

  const recentMax = Math.max(...recent.map((d) => d.snow), 1);
  const recentTotal = recent.reduce((s, d) => s + d.snow, 0);
  const forecastTotal = forecast.reduce((s, d) => s + d.snow, 0);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-stretch">
      <section className="flex flex-col">
        <MiniSectionHeading
          title={t('resort.skiReport.recentSnowfall')}
          href={snowHistoryHref}
          ctaLabel={t('resort.skiReport.historicalSnowfall')}
        />
        <div className="flex flex-1 flex-col rounded-2xl border border-border bg-card p-5">
          {state === 'expired' ? (
            <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
              <CloudOff size={28} className="mb-2 fill-none text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                {t('resort.skiReport.noRecentSnow')}
              </p>
              {reportedAtLabel && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {`${t('resort.skiReport.lastUpdated')} ${reportedAtLabel}`}
                </p>
              )}
            </div>
          ) : (
            <>
              {state === 'stale' && (
                <div role="status" className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <Info size={14} className="mt-0.5 shrink-0 fill-none" />
                  <span>{t('resort.skiReport.reportStale', { date: reportedAtLabel })}</span>
                </div>
              )}
              <div className="mb-5">
                <Text variant="eyebrow">{t('resort.skiReport.fiveDayTotal')}</Text>
                <div
                  className={cn(
                    'mt-1 font-heading text-4xl font-bold leading-none tabular-nums sm:text-5xl',
                    state === 'stale' ? 'text-muted-foreground' : 'text-primary',
                  )}
                >
                  {dt(recentTotal)}
                </div>
              </div>
              <div className="mt-auto flex items-end gap-2">
                {recent.map((d, idx) => {
                  const isRecent = idx === recent.length - 1;
                  const barFill = barFillClass(state === 'stale', isRecent);
                  return (
                    <div key={d.date} className="flex flex-1 flex-col items-center gap-1.5">
                      <div className="flex h-28 w-full items-end">
                        <div
                          className={cn('w-full rounded-sm', barFill)}
                          style={{ height: `${Math.max(8, (d.snow / recentMax) * 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold tabular-nums">{dt(d.snow)}</span>
                      <span className="text-xs text-muted-foreground">
                        {barLabel(d.date, state, t('common.dayToday'))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="flex flex-col">
        <MiniSectionHeading
          title={t('resort.skiReport.forecastedSnow')}
          href={fullWeatherHref}
          ctaLabel={t('resort.skiReport.fullWeatherForecast')}
        />
        <div className="flex flex-1 flex-col rounded-2xl border border-border bg-card p-5">
          <div className="mb-5">
            <Text variant="eyebrow">{t('resort.skiReport.fiveDayForecast')}</Text>
            <div className="mt-1 font-heading text-4xl font-bold leading-none text-primary tabular-nums sm:text-5xl">
              {dt(forecastTotal)}
            </div>
          </div>
          <div className="mt-auto grid grid-cols-5 gap-2">
            {forecast.map((d, idx) => {
              const Icon = iconForSnow(d.snow);
              const isToday = idx === 0;
              const dayTemp = weatherDays[idx]?.base?.temp;
              return (
                <div
                  key={d.date}
                  className="flex flex-col items-center justify-between gap-1.5 rounded-md border border-border bg-background p-3 text-center"
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {forecastDayLabel(d.date, isToday, t('common.dayToday'))}
                  </span>
                  <Icon size={22} className={d.snow > 0 ? 'text-primary' : 'text-muted-foreground'} />
                  <span className="text-sm font-semibold tabular-nums">
                    {dt(d.snow)}
                  </span>
                  {dayTemp?.max !== undefined && dayTemp?.min !== undefined ? (
                    <div className="flex items-baseline gap-1 text-xs tabular-nums">
                      <span className="font-semibold text-foreground">{tempFmt(dayTemp.max)}</span>
                      <span className="text-muted-foreground">{tempFmt(dayTemp.min)}</span>
                    </div>
                  ) : (
                    <div className="h-3" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
